package modysy.simulation

import modysy.datastructures.Vector2
import modysy.model._

package object collision {

  def close(
      value: Double,
      otherValue: Double,
      relativeTolerance: Double = 1e-4
  ): Boolean = {
    val divisor =
      if (Math.abs(value) + Math.abs(otherValue) > relativeTolerance)
        Math.abs(value) + Math.abs(otherValue)
      else 1

    (value - otherValue) / divisor > relativeTolerance
  }

  /** Return collision between two objects if present or None otherwise. */
  private[collision] def findCollision(
      obj: GameObject,
      otherObject: GameObject
  ): Option[CollisionPair] = {
    val relativeVelocity = otherObject.body.velocity.linear - obj.body.velocity.linear

    /*TODO look at boxCircleCollision: from the logical viewpoint, the relative velocity in its call should
       be negated, but it works like it is now. This is confusing
     */
    val calculatedCollision =
      (obj.body.geometry, otherObject.body.geometry) match {
        case (Circle(radius), Circle(otherRadius)) =>
          circleCollision(
            obj.body.pose.translation,
            radius,
            otherObject.body.pose.translation,
            otherRadius,
            relativeVelocity
          )
        case (Circle(radius), b: Box) =>
          boxCircleCollision(
            otherObject.body.pose,
            b,
            obj.body.pose.translation,
            radius,
            relativeVelocity
          )
        case (b: Box, Circle(radius)) =>
          boxCircleCollision(
            obj.body.pose,
            b,
            otherObject.body.pose.translation,
            radius,
            -relativeVelocity
          ) map { spot =>
            CollisionSpot(spot.point, -spot.normal, spot.penetration)
          }
        case _ => None
      }

    calculatedCollision map {
      CollisionPair(obj, otherObject, _)
    }
  }

  /** Return the bounding box of obj. */
  private[collision] def boundingBox(obj: GameObject): AxisAlignedBoundingBox =
    obj.body.geometry match {
      case Circle(radius) =>
        AxisAlignedBoundingBox(
          (
            obj.body.pose.translation.x - radius,
            obj.body.pose.translation.y + radius
          ),
          (
            obj.body.pose.translation.x + radius,
            obj.body.pose.translation.y - radius
          )
        )
      case Box(width, height) =>
        val bottomLeft = Vector2(-width / 2, height / 2)
        val topRight = Vector2(width / 2, -height / 2)

        val points = AxisAlignedBoundingBox(bottomLeft, topRight).points map {
          obj.body.pose * _
        }
        val minPoint =
          points.reduceLeft((a, b) => Vector2(a.x min b.x, a.y min b.y))
        val maxPoint =
          points.reduceLeft((a, b) => Vector2(a.x max b.x, a.y max b.y))

        AxisAlignedBoundingBox(
          (minPoint.x, maxPoint.y),
          (maxPoint.x, minPoint.y)
        )
    }

  /** Compute list of collision points between two circles.
    *
    * Currently the convention is that there is one collision point between circles, in the middle of their
    * overlapping region.
    */
  private def circleCollision(
      position: Vector2,
      radius: Double,
      otherPosition: Vector2,
      otherRadius: Double,
      relativeVelocity: Vector2
  ): Option[CollisionSpot] = {
    val squaredDistance = (position - otherPosition).squaredNorm

    if (squaredDistance < (radius + otherRadius) * (radius + otherRadius)) { // distance from center to center so size from both matters
      val distance = Math.sqrt(squaredDistance)
      val normal =
        if (close(distance, 0))
          (otherPosition - position).unit
        // normal could be arbitrary when the centers are close
        // if relative velocity nonzero, we choose a normal opposite of it so the objects are on non-separating course
        // (look at collision resolution code)
        else {
          if (!relativeVelocity.closeTo((0, 0))) -relativeVelocity.unit
          else Vector2(1, 0)
        }

      val middlePoint =
        if (close(distance, 0))
          position + normal * (distance - otherRadius + Math.min(
            radius,
            distance
          )) / 2
        else position

      val penetration = radius + otherRadius - distance

      Some(CollisionSpot(middlePoint, normal, penetration))
    } else None
  }

  private def boxCircleCollision(
      boxPose: Transform,
      box: Box,
      circlePosition: Vector2,
      circleRadius: Double,
      relativeVelocity: Vector2
  ): Option[CollisionSpot] = {
    val distance = circlePosition - boxPose.translation

    // rotate distance so box is aligned with axes
    val distanceBoxFrame = boxPose.orientationPart.inverse * distance

    // clamp distance so it is at maximum at the border of the box
    distanceBoxFrame.x = -box.width / 2 max distanceBoxFrame.x min box.width / 2
    distanceBoxFrame.y = -box.height / 2 max distanceBoxFrame.y min box.height / 2

    // rotate the distance back
    def closestAndDirection = () => {
      val closest = boxPose * distanceBoxFrame
      (closest, circlePosition - closest)
    }

    val (closestBoxPoint, direction) = closestAndDirection()
    if (direction.squaredNorm > circleRadius * circleRadius)
      // box and circle do not touch
      None
    else if (closestBoxPoint != circlePosition)
      // circle center outside of the box
      Some(
        CollisionSpot(
          closestBoxPoint,
          -direction.unit,
          circleRadius - direction.norm
        )
      )
    else {
      // circle center inside of the box
      if (box.width / 2 - math.abs(distanceBoxFrame.x) < box.height / 2 - math
            .abs(distanceBoxFrame.y)) {
        distanceBoxFrame.x = math.signum(distanceBoxFrame.x) * box.width / 2
      } else {
        distanceBoxFrame.y = math.signum(distanceBoxFrame.y) * box.height / 2
      }

      val (closestBoxPoint, direction) = closestAndDirection()
      val normal =
        if (!direction.closeTo((0, 0))) direction.unit
        else {
          if (!relativeVelocity.closeTo((0, 0))) -relativeVelocity.unit
          else Vector2(0, 0)
        }

      Some(
        CollisionSpot(closestBoxPoint, normal, circleRadius + direction.norm)
      )
    }
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/simulation/collision/package.scala